import { Button } from "@/components/ui/button";
import { MapPin, Clock, Euro, Bookmark } from "lucide-react";
import { Card } from "@/components/ui/card";
import { EmploymentTypeBadge } from "@/components/EmploymentTypeBadge";
import { WorkLocationBadge } from "@/components/WorkLocationBadge";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { resolveBannerUrl, defaultBanner, isFlatDefaultBanner } from "@/lib/bannerUtils";
import { stripHtmlPreview } from "@/lib/stripHtmlPreview";
import { useState } from "react";
import { SignupPromptDialog } from "@/components/SignupPromptDialog";

interface JobCardProps {
  id?: string;
  slug?: string;
  title: string;
  description?: string;
  company: string;
  location: string;
  hours: string;
  salary: string;
  tags?: string[];
  featured?: boolean;
  coverImage?: string;
  isBoosted?: boolean;
  companyLogo?: string;
  isPremium?: boolean;
  employmentType?: string;
  workLocation?: string;
  fromCompany?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
}


export const JobCard = ({ 
  id,
  slug,
  title,
  description,
  company, 
  location, 
  hours, 
  salary, 
  tags,
  featured = false,
  coverImage,
  isBoosted = false,
  companyLogo,
  isPremium = false,
  employmentType,
  workLocation,
  fromCompany = false,
  onClick,
  onMouseEnter,

}: JobCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isNavigating, setIsNavigating] = useState(false);
  const [signupPromptOpen, setSignupPromptOpen] = useState(false);

  // Check if job is saved
  const { data: isSaved = false } = useQuery({
    queryKey: ['is-job-saved', id, user?.id],
    queryFn: async () => {
      if (!user?.id || !id) return false;

      const { data } = await supabase
        .from('saved_jobs')
        .select('id')
        .eq('user_id', user.id)
        .eq('job_id', id)
        .maybeSingle();

      return !!data;
    },
    enabled: !!user?.id && !!id
  });

  // Check if this is a featured demo job
  const isFeaturedJob = id?.startsWith('featured-');

  // Save/unsave mutation
  const toggleSaveMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !id) {
        console.error('[JobCard] Save failed: Missing user or job ID', { user: !!user, id });
        throw new Error("Je moet ingelogd zijn om vacatures op te slaan");
      }

      // Prevent saving featured demo jobs
      if (isFeaturedJob) {
        console.warn('[JobCard] Attempted to save featured demo job', { id });
        throw new Error("Demo vacatures kunnen niet worden opgeslagen");
      }

      try {
        if (isSaved) {
          // Unsave
          console.log('[JobCard] Unsaving job', { id });
          const { error } = await supabase
            .from('saved_jobs')
            .delete()
            .eq('user_id', user.id)
            .eq('job_id', id);

          if (error) {
            console.error('[JobCard] Unsave error:', error);
            throw error;
          }
        } else {
          // Save
          console.log('[JobCard] Saving job', { id });
          const { error } = await supabase
            .from('saved_jobs')
            .insert({
              user_id: user.id,
              job_id: id
            });

          if (error) {
            console.error('[JobCard] Save error:', error);
            throw error;
          }
        }
      } catch (error) {
        console.error('[JobCard] Mutation error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['is-job-saved', id] });
      queryClient.invalidateQueries({ queryKey: ['saved-jobs'] });
      console.log('[JobCard] Save/unsave successful', { id, wasSaved: isSaved });
      toast({
        title: isSaved ? "Vacature verwijderd" : "Vacature opgeslagen",
        description: isSaved 
          ? "Deze vacature is verwijderd uit je opgeslagen vacatures"
          : "Je kunt deze vacature terugvinden bij 'Opgeslagen'"
      });
    },
    onError: (error: any) => {
      console.error('[JobCard] Save/unsave error:', error);
      toast({
        title: "Fout bij opslaan",
        description: error.message || "Er ging iets mis bij het opslaan van deze vacature",
        variant: "destructive"
      });
    }
  });
  
  const handleClick = () => {
    if ((!id && !slug) || isNavigating) {
      console.warn('[JobCard] Clicked without ID/slug or already navigating - navigation skipped');
      return;
    }

    // Allow parent (e.g. PublicJobs) to persist filters / seed cache before navigation
    try { onClick?.(); } catch (e) { console.warn('[JobCard] onClick handler error', e); }

    
    // Save scroll position before navigating away from /vacatures
    if (window.location.pathname === '/vacatures') {
      sessionStorage.setItem('vacaturesScrollPos', window.scrollY.toString());
    }
    
    console.log('[JobCard] Clicked:', { id, slug, title, isFeatured: id?.startsWith('featured-') });
    setIsNavigating(true);
    
    if (id?.startsWith('featured-')) {
      navigate('/vacatures');
      return;
    }
    
    const urlParam = slug || id;
    const navOpts = fromCompany ? { state: { fromCompany: true } } : undefined;
    // If navigating from homepage, push /vacatures first so the back button returns there
    if (window.location.pathname === '/') {
      navigate('/vacatures');
      navigate(`/vacatures/${urlParam}`, navOpts);
      return;
    }
    navigate(`/vacatures/${urlParam}`, navOpts);

  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    
    if (!user) {
      console.log('[JobCard] Save clicked but user not logged in - showing signup prompt');
      setSignupPromptOpen(true);
      return;
    }

    if (isFeaturedJob) {
      console.log('[JobCard] Save clicked on featured job - showing tooltip');
      toast({
        title: "Demo vacature",
        description: "Dit is een demo vacature die niet opgeslagen kan worden",
      });
      return;
    }

    toggleSaveMutation.mutate();
  };
  
  return (
    <>
    <Card 
      role="link"
      tabIndex={0}
      aria-label={`Vacature ${title}${company ? ` bij ${company}` : ""} bekijken`}
      className={`
        relative overflow-hidden
        bg-card
        border border-border
        rounded-3xl
        shadow-none
        hover:border-cyan
        transition-colors duration-200
        flex flex-col h-full
        cursor-pointer group
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2
        ${featured ? "border-yellow ring-1 ring-yellow/20" : ""}
        ${isNavigating ? 'opacity-50 pointer-events-none' : ''}
      `}
      onClick={handleClick}
      onKeyDown={(e) => {
        // Only the card itself: Enter on the bookmark button must not also open the job
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      onMouseEnter={onMouseEnter}
      onFocus={onMouseEnter}
    >
      {/* Cover Image - Premium gets custom banner, free gets gradient */}
      <div className="relative w-full aspect-[3/1] overflow-hidden">
        {coverImage ? (
          <>
            <img 
              src={resolveBannerUrl(coverImage)}
              alt={`${title} — ${company}${location ? ` in ${location}` : ''}`}
              className="w-full h-full object-cover object-center group-hover:scale-[1.06] transition-transform duration-700 ease-out"
              loading="lazy"
              decoding="async"
              width={640}
              height={360}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            {!isFlatDefaultBanner(coverImage) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            )}
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan/15 via-cyan/5 to-primary/5 flex items-center justify-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan/20 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
            <span className="text-5xl font-heading font-black text-cyan/20 select-none drop-shadow-sm group-hover:scale-110 transition-transform duration-500">
              {company.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        
        {/* Company logo - always shown, fallback to initial */}
        <div className="absolute bottom-3 left-3">
          <div className="relative">
            
            {companyLogo ? (
              <img 
                src={companyLogo} 
                alt={`Logo ${company}`}
                className="relative w-12 h-12 rounded-xl object-cover shadow-lg bg-card ring-1 ring-foreground/10"
                loading="lazy"
                decoding="async"
                width={48}
                height={48}
              />
            ) : (
              <div
                className="relative w-12 h-12 rounded-xl border-2 border-background shadow-lg bg-background flex items-center justify-center"
                aria-label={`Logo ${company}`}
              >
                <span className="text-xl font-heading font-black text-cyan">
                  {company.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {featured && (
          <div className="absolute top-3 right-3 bg-yellow text-yellow-foreground px-4 py-1 rounded-lg font-bold text-sm shadow-lg">
            ⭐ Featured
          </div>
        )}
        
        {isBoosted && !featured && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-lg font-bold text-sm shadow-lg flex items-center gap-1.5 animate-pulse">
            🔥 Geboost
          </div>
        )}



        {/* Save Button */}
        <Button
          size="icon"
          variant="ghost"
          disabled={isFeaturedJob || toggleSaveMutation.isPending}
          className={`absolute top-3 right-3 bg-background/80 backdrop-blur-sm hover:bg-background hover:scale-110 transition-all ${
            isSaved ? "text-yellow" : "text-muted-foreground"
          } ${isFeaturedJob ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleSaveClick}
          title={isFeaturedJob ? "Demo vacature - kan niet worden opgeslagen" : isSaved ? "Verwijder uit opgeslagen" : "Opslaan"}
        >
          <Bookmark className={`h-5 w-5 ${isSaved ? "fill-current" : ""}`} />
        </Button>
      </div>
      
      <div className="p-5 md:p-6 flex flex-col flex-grow">
        {/* Title + location wrapper — reserves space for 2-line title so chips align across cards, but location sits directly under the actual title */}
        <div className="min-h-[5rem] md:min-h-[5.25rem] mb-4">
          {/* Title — max 2 lines */}
          <h3 className="text-xl md:text-2xl font-heading font-bold leading-tight line-clamp-2 mb-1">
            {title}
          </h3>

          {/* Location + company — single line, truncate, directly under title */}
          <div className="flex items-center gap-1.5 text-sm text-foreground/60 font-semibold tracking-wide h-5 overflow-hidden whitespace-nowrap">
            {location && (
              <span className="inline-flex items-center gap-1 shrink-0">
                <MapPin className="w-3.5 h-3.5 text-cyan" />
                <span>{location}</span>
              </span>
            )}
            {location && <span className="text-foreground/30 shrink-0">·</span>}
            <span className="truncate">{company}</span>
          </div>
        </div>

        {/* Chips row — fixed height, chips wrap but container clips */}
        <div className="min-h-[76px] flex flex-wrap items-start content-start gap-2 mb-4 overflow-hidden">
          {salary && salary.toLowerCase() !== "in overleg" && (
            <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 px-3 py-1.5 rounded-xl text-sm">
              <Euro className="w-4 h-4 text-green-600" />
              <span className="font-bold text-green-600">{salary}</span>
            </div>
          )}
          {hours && (
            <div className="flex items-center gap-1.5 bg-foreground/[0.03] border border-foreground/[0.06] px-3 py-1.5 rounded-xl text-sm">
              <Clock className="w-4 h-4 text-cyan" />
              <span className="font-semibold text-foreground/70">{hours}</span>
            </div>
          )}
          <EmploymentTypeBadge type={employmentType} />
          <WorkLocationBadge type={workLocation} />
        </div>

        {/* Description preview — always exactly 2 lines */}
        <div className="mb-4 p-3 bg-cyan-light/30 rounded-xl border border-cyan/20">
          <p className="text-xs text-foreground/50 font-medium uppercase tracking-wide mb-1">
            Over de functie
          </p>
          <p className="text-sm text-foreground/70 line-clamp-2 h-[2.5rem]">
            {stripHtmlPreview(description) || "Geen beschrijving beschikbaar."}
          </p>
        </div>
        {/* Geen aparte knop: de hele kaart is klikbaar */}
      </div>
    </Card>
    <SignupPromptDialog
      open={signupPromptOpen}
      onOpenChange={setSignupPromptOpen}
      context="save"
      companyName={company}
      jobTitle={title}
    />
    </>
  );
};
